/**
 * NightlyReconcileTile — admin-only Overview-tab widget that surfaces
 * the last nightly reconcile run so ops can see silent-drop self-heal
 * status at a glance without tailing logs.
 *
 * Data flow:
 *   • Mount → GET /api/admin/nightly-reconcile/status?limit=1
 *   • Renders per-step status pills (gcal / material-costs /
 *     profitability) with color coded by ok / skipped / error.
 *   • "Run now" button → POST /api/admin/nightly-reconcile/run-now.
 *     Blocks for the duration of the run (typically <60s) then
 *     refreshes the tile from the fresh audit doc.
 *
 * Backend endpoints (both admin-gated):
 *   GET  /api/admin/nightly-reconcile/status?limit=N   (last N audit docs)
 *   POST /api/admin/nightly-reconcile/run-now          (fires reconcile now)
 *
 * Cron target: 03:00 Europe/Dublin (see services/nightly_reconcile.py).
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  MinusCircle,
  ShieldCheck,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human labels for the three reconcile steps — keeps rendering DRY.
const STEP_LABELS = {
  gcal_reconcile: "Google Calendar",
  material_costs_backfill: "Material Costs",
  job_profitability_backfill: "Profitability",
};

// Compact relative-time formatter — avoids pulling in date-fns just
// for this one tile. Any run older than 48h falls back to a full ISO
// date so "2 days ago" doesn't hide a stuck cron.
const formatRelative = (isoString) => {
  if (!isoString) return "never";
  const then = new Date(isoString);
  const secs = Math.max(0, (Date.now() - then.getTime()) / 1000);
  if (secs < 60) return `${Math.round(secs)}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  if (secs < 172800) return "yesterday";
  return then.toISOString().slice(0, 16).replace("T", " ") + " UTC";
};

// Renders a single step pill. Ok=green, skipped=slate, error=red.
const StepPill = ({ name, step }) => {
  const label = STEP_LABELS[name] || name;
  const status = step?.status || "unknown";

  let icon = null;
  let color = "";
  let detail = "";

  if (status === "ok") {
    icon = <CheckCircle2 className="w-3.5 h-3.5" />;
    color = "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    const r = step.result || {};
    if ("attempted" in r) {
      detail = `${r.synced || 0}/${r.attempted || 0} synced`;
    } else if ("updated" in r) {
      detail = `${r.updated || 0} updated`;
    }
  } else if (status === "skipped") {
    icon = <MinusCircle className="w-3.5 h-3.5" />;
    color = "bg-slate-500/15 text-slate-300 border-slate-500/30";
    detail = step.reason || "skipped";
  } else if (status === "error") {
    icon = <AlertTriangle className="w-3.5 h-3.5" />;
    color = "bg-red-500/15 text-red-300 border-red-500/30";
    detail = (step.error || "error").slice(0, 60);
  } else {
    icon = <MinusCircle className="w-3.5 h-3.5" />;
    color = "bg-slate-500/15 text-slate-400 border-slate-500/30";
  }

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${color}`}
      data-testid={`reconcile-step-${name}`}
      title={detail || label}
    >
      {icon}
      <span className="font-medium">{label}</span>
      {detail ? <span className="opacity-80 truncate max-w-[9rem]">— {detail}</span> : null}
    </div>
  );
};

const NightlyReconcileTile = () => {
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/nightly-reconcile/status?limit=1`, {
        withCredentials: true,
      });
      const runs = r?.data?.runs || [];
      setLastRun(runs[0] || null);
    } catch (e) {
      // 401/403 is the expected non-admin path — surface it quietly.
      // Any other error is a real problem worth showing.
      const code = e?.response?.status;
      if (code === 401 || code === 403) {
        setError("admin-only");
      } else {
        setError(e?.response?.data?.detail || e.message || "Failed to load");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(
        `${API}/admin/nightly-reconcile/run-now`,
        {},
        { withCredentials: true, timeout: 120_000 },
      );
      setLastRun(r?.data || null);
      const status = r?.data?.overall_status;
      if (status === "ok") {
        toast.success("Reconcile finished — all steps green");
      } else {
        toast.error("Reconcile finished with errors — see step details");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile run failed");
    } finally {
      setRunning(false);
    }
  };

  // Non-admin path (401/403) — hide the tile entirely instead of
  // showing a broken-looking card.
  if (error === "admin-only") return null;

  return (
    <Card
      className="bg-slate-800/50 border-slate-700"
      data-testid="nightly-reconcile-tile"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <ShieldCheck className="w-4 h-4 text-saturn-orange" />
            Nightly Reconcile
            {lastRun?.overall_status === "ok" && (
              <Badge className="ml-1 bg-emerald-500/20 text-emerald-300 border-none text-xs">
                All Green
              </Badge>
            )}
            {lastRun?.overall_status === "error" && (
              <Badge className="ml-1 bg-red-500/20 text-red-300 border-none text-xs">
                Needs Attention
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchStatus}
              disabled={loading || running}
              className="border-slate-700 text-slate-300 h-7 text-xs"
              data-testid="reconcile-refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              size="sm"
              onClick={runNow}
              disabled={running}
              className="bg-saturn-orange hover:bg-saturn-orange/90 h-7 text-xs"
              data-testid="reconcile-run-now-btn"
            >
              {running ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Running…
                </>
              ) : (
                "Run Now"
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <p className="text-slate-500 text-sm">Loading last run…</p>
        ) : error && error !== "admin-only" ? (
          <p className="text-red-400 text-sm" data-testid="reconcile-load-error">
            {error}
          </p>
        ) : !lastRun ? (
          <p className="text-slate-400 text-sm" data-testid="reconcile-never-run">
            No runs recorded yet. Scheduled nightly at 03:00 Europe/Dublin — or hit
            <span className="text-slate-200"> Run Now </span> to fire immediately.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-xs text-slate-400 flex-wrap">
              <span data-testid="reconcile-last-run-at">
                Last run: <span className="text-slate-200">{formatRelative(lastRun.started_at)}</span>
              </span>
              <span>·</span>
              <span>
                Duration: <span className="text-slate-200">{lastRun.duration_seconds}s</span>
              </span>
              <span>·</span>
              <span>
                Trigger: <span className="text-slate-200">{lastRun.triggered_by}</span>
              </span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="reconcile-steps">
              {Object.entries(lastRun.steps || {}).map(([name, step]) => (
                <StepPill key={name} name={name} step={step} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NightlyReconcileTile;
